import { useState } from 'react'
import { colors, fontFamily, fontSize, radius, shadow, spacing } from '../styles/theme'
import BaseApiService from '../services/BaseApiService'
import { Select } from '../components/ui'

const PROVIDERS = ['bigquery', 'snowflake', 'redshift'] as const
type Provider = typeof PROVIDERS[number]

const CREDENTIAL_FIELDS: Record<Provider, { key: string; label: string; type?: string }[]> = {
  bigquery:  [{ key: 'projectId', label: 'Project ID' }, { key: 'accessToken', label: 'Access Token', type: 'password' }],
  snowflake: [
    { key: 'account', label: 'Account (e.g. xyz123.us-east-1)' },
    { key: 'accessToken', label: 'OAuth Access Token', type: 'password' },
    { key: 'warehouse', label: 'Warehouse' },
    { key: 'database', label: 'Database' },
    { key: 'schema', label: 'Schema (default: PUBLIC)' },
  ],
  redshift: [
    { key: 'host', label: 'Host' },
    { key: 'port', label: 'Port (default: 5439)' },
    { key: 'database', label: 'Database' },
    { key: 'username', label: 'Username' },
    { key: 'password', label: 'Password', type: 'password' },
  ],
}

class WarehouseApiService extends BaseApiService {
  renderWarehouse(body: object): Promise<Blob> {
    return this.postBlob('/v1/render/warehouse', body)
  }
}

const warehouseApi = new WarehouseApiService()

const input: React.CSSProperties = {
  width: '100%',
  padding: `8px ${spacing.sm}`,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.sm,
  fontSize: fontSize.sm,
  color: colors.text,
  background: colors.surface,
  boxSizing: 'border-box',
}

export default function WarehousePage() {
  const [provider, setProvider] = useState<Provider>('bigquery')
  const [creds, setCreds] = useState<Record<string, string>>({})
  const [query, setQuery] = useState('')
  const [templateVersionId, setTemplateVersionId] = useState('')
  const [outputFormat, setOutputFormat] = useState('pdf')
  const [rowMode, setRowMode] = useState<'first' | 'array'>('first')
  const [maskFields, setMaskFields] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ url: string; name: string } | null>(null)

  function setCred(key: string, value: string) {
    setCreds(prev => ({ ...prev, [key]: value }))
  }

  async function handleRender() {
    if (!query.trim() || !templateVersionId.trim()) {
      setError('SQL query and template version ID are required.')
      return
    }
    setLoading(true)
    setError(null)
    setResult(null)
    try {
      const blob = await warehouseApi.renderWarehouse({
        provider,
        query,
        credentialsJson: creds,
        templateVersionId,
        outputFormat,
        rowMode,
        maskFields: maskFields.split(',').map(s => s.trim()).filter(Boolean),
      })
      const url = URL.createObjectURL(blob)
      setResult({ url, name: `output.${outputFormat}` })
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Render failed.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h1 style={{ fontSize: fontSize['2xl'], fontWeight: 600, letterSpacing: '-0.02em', color: colors.text, marginBottom: spacing.lg }}>
        Warehouse Render
      </h1>

      <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
        <div>
          <label htmlFor="warehouse-provider" style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: 4 }}>
            Provider
          </label>
          <Select id="warehouse-provider"
            value={provider}
            onChange={v => { setProvider(v as Provider); setCreds({}) }}
            options={PROVIDERS.map(p => ({ value: p, label: p }))}
          />
        </div>

        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: radius.md, boxShadow: shadow.xs, padding: spacing.md }}>
          <div style={{ fontSize: fontSize.sm, fontWeight: 600, color: colors.textSecondary, marginBottom: spacing.sm }}>
            Credentials
          </div>
          <div style={{ display: 'grid', gap: spacing.sm }}>
            {CREDENTIAL_FIELDS[provider].map(f => (
              <div key={f.key}>
                <label htmlFor={`warehouse-cred-${f.key}`} style={{ display: 'block', fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: 2 }}>
                  {f.label}
                </label>
                <input id={`warehouse-cred-${f.key}`}
                  type={f.type ?? 'text'}
                  value={creds[f.key] ?? ''}
                  onChange={e => setCred(f.key, e.target.value)}
                  style={input}
                />
              </div>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="warehouse-sql-query" style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: 4 }}>
            SQL Query
          </label>
          <textarea id="warehouse-sql-query"
            value={query}
            onChange={e => setQuery(e.target.value)}
            rows={5}
            placeholder="SELECT * FROM orders WHERE id = 123"
            style={{ ...input, fontFamily: fontFamily.mono, resize: 'vertical' }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
          <div>
            <label htmlFor="warehouse-template-version-id" style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: 4 }}>
              Template Version ID
            </label>
            <input id="warehouse-template-version-id"
              value={templateVersionId}
              onChange={e => setTemplateVersionId(e.target.value)}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              style={input}
            />
          </div>
          <div>
            <label htmlFor="warehouse-output-format" style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: 4 }}>
              Output Format
            </label>
            <Select id="warehouse-output-format"
              value={outputFormat}
              onChange={v => setOutputFormat(String(v))}
              options={['pdf','docx','xlsx','pptx','html','txt','csv'].map(f => ({ value: f, label: f }))}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
          <div>
            <label htmlFor="warehouse-row-mode" style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: 4 }}>
              Row Mode
            </label>
            <Select id="warehouse-row-mode"
              value={rowMode}
              onChange={v => setRowMode(v as 'first' | 'array')}
              options={[
                { value: 'first', label: 'first — single row as flat object' },
                { value: 'array', label: 'array — all rows as {rows: [...]}' },
              ]}
            />
          </div>
          <div>
            <label htmlFor="warehouse-mask-fields-comma" style={{ display: 'block', fontSize: fontSize.sm, fontWeight: 600, color: colors.text, marginBottom: 4 }}>
              Mask Fields (comma-separated)
            </label>
            <input id="warehouse-mask-fields-comma"
              value={maskFields}
              onChange={e => setMaskFields(e.target.value)}
              placeholder="ssn, creditCard, email"
              style={input}
            />
          </div>
        </div>

        {error && (
          <div style={{ background: colors.errorLight, border: `1px solid ${colors.errorBorder}`, borderRadius: radius.sm, padding: spacing.md, color: colors.error, fontSize: fontSize.sm }}>
            {error}
          </div>
        )}

        {result && (
          <div style={{ background: colors.successLight, border: `1px solid ${colors.successBorder}`, borderRadius: radius.sm, padding: spacing.md, display: 'flex', alignItems: 'center', gap: spacing.md }}>
            <span style={{ color: colors.success, fontSize: fontSize.sm, flex: 1 }}>Render complete</span>
            <a
              href={result.url}
              download={result.name}
              style={{ background: colors.primary, color: colors.onPrimary, padding: `6px ${spacing.md}`, borderRadius: radius.sm, fontSize: fontSize.sm, textDecoration: 'none', fontWeight: 600 }}
            >
              Download {result.name}
            </a>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={handleRender}
            disabled={loading}
            style={{
              background: loading ? colors.textMuted : colors.primary,
              color: colors.onPrimary,
              border: 'none',
              borderRadius: radius.sm,
              padding: `10px ${spacing.lg}`,
              fontSize: fontSize.sm,
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
            }}
          >
            {loading ? 'Rendering...' : 'Run & Render'}
          </button>
        </div>
      </div>
    </div>
  )
}
